import { inject, Injectable, isDevMode, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as fromShared from '@shared';
import { Socket, SocketIoConfig } from 'ngx-socket-io';
import { debounceTime, merge, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import * as fromGenerated from '../_generated';
import * as fromAuth from '../auth';
import { AppActions } from '../store/app.actions';
import { AppState } from '../store/app.reducer';
import * as fromAssistants from './assistants';
import { ChatWithMessagesActions } from './store/chats.actions';
import { ChatsState } from './store/chats.reducer';
import { TeammatesActions } from './teammates/store';

export const chatsSocketConfig: SocketIoConfig = {
  url: `${environment.appApiUrl}/chats`,
  options: {
    autoConnect: false,
    reconnection: true,
    closeOnBeforeunload: true,
    withCredentials: true,
    protocols: ['polling', 'websocket'],
    upgrade: true,
  },
};

/** Coalesce bursts of access-change events (and the initial connect) into one reload. */
const ACCESS_RELOAD_DEBOUNCE_MS = 300;

interface ApiError {
  message: string;
  name: string;
  response: {
    error: string;
    message: string;
    statusCode: number;
  };
  status: number;
}

@Injectable()
export class ChatsSocket {
  private readonly store = inject<Store<ChatsState>>(Store);
  private readonly appStore = inject<Store<AppState>>(Store);
  private readonly translateService = inject(TranslateService);
  private readonly socket = inject<Socket>(Socket);
  private readonly ngZone = inject(NgZone);

  constructor() {
    this.ngZone.runOutsideAngular(() => {
      this.socket
        .fromEvent<fromShared.VersionCheckEventPayload>(
          fromShared.ChatsGatewayEventsType.VersionCheck,
        )
        .pipe(
          takeUntilDestroyed(),
          tap(({ apiReleaseVersion }) => {
            this.ngZone.run(() => {
              this.appStore.dispatch(
                AppActions.setApiReleaseVersion({
                  apiReleaseVersion,
                }),
              );
            });
          }),
        )
        .subscribe();

      this.socket
        .fromEvent<{
          type: string;
          chatWithMessages: fromGenerated.ChatWithMessagesView;
        }>(fromShared.ChatsGatewayEventsType.ChatMessageAnswered)
        .pipe(
          takeUntilDestroyed(),
          tap(({ chatWithMessages }) => {
            this.ngZone.run(() => {
              this.store.dispatch(
                ChatWithMessagesActions.receivedChatWithMessagesFromSocketSuccess(
                  {
                    chatWithMessages,
                  },
                ),
              );
            });
          }),
        )
        .subscribe();

      this.socket
        .fromEvent<{
          type: string;
          chatWithMessages: fromGenerated.ChatWithMessagesView;
          errorCode: string;
        }>(fromShared.ChatsGatewayEventsType.ChatMessageError)
        .pipe(
          takeUntilDestroyed(),
          tap(({ chatWithMessages, errorCode }) => {
            console.error(`Error received from socket, code "${errorCode}"`);

            this.ngZone.run(() => {
              this.store.dispatch(
                ChatWithMessagesActions.receivedChatWithMessagesFromSocketFailure(
                  {
                    chatWithMessages,
                    errorCode,
                  },
                ),
              );
            });
          }),
        )
        .subscribe();

      this.socket
        .fromEvent<{
          type: string;
          uuid: fromGenerated.AssistantView['uuid'];
          status: fromGenerated.AssistantView['status'];
          documents: fromGenerated.AssistantView['documents'];
        }>(fromShared.ChatsGatewayEventsType.AssistantsStatusUpdated)
        .pipe(
          takeUntilDestroyed(),
          tap<{
            uuid: fromGenerated.AssistantView['uuid'];
            status: fromGenerated.AssistantView['status'];
            documents: fromGenerated.AssistantView['documents'];
          }>(({ uuid, status, documents }) => {
            this.store.dispatch(
              fromAssistants.AssistantActions.receivedAssistantStatusUpdatedFromSocket(
                {
                  uuid,
                  status,
                  documents,
                },
              ),
            );
          }),
        )
        .subscribe();

      this.socket
        .fromEvent<{
          type: string;
          userId: string;
          chatId: number;
          chatMessageId: number;
          documentCitations: fromGenerated.DocumentCitationView[];
        }>(fromShared.ChatsGatewayEventsType.ChatMessageDocumentCitationsAdded)
        .pipe(
          takeUntilDestroyed(),
          tap(({ chatId, chatMessageId, documentCitations }) => {
            this.ngZone.run(() => {
              this.store.dispatch(
                ChatWithMessagesActions.receivedDocumentCitationsFromSocketSuccess(
                  {
                    chatId,
                    chatMessageId,
                    documentCitations,
                  },
                ),
              );
            });
          }),
        )
        .subscribe();

      this.socket
        .fromEvent<{
          type: string;
          userId: string;
          chatUuid: string;
          documentId: string;
          documentStatus: fromGenerated.DocumentView['status'];
        }>(fromShared.ChatsGatewayEventsType.ChatDocumentsStatusUpdated)
        .pipe(
          takeUntilDestroyed(),
          tap(({ chatUuid, documentId, documentStatus }) => {
            this.ngZone.run(() => {
              this.store.dispatch(
                ChatWithMessagesActions.receivedDocumentStatusUpdatedFromSocketSuccess(
                  {
                    chatUuid,
                    documentId,
                    documentStatus,
                  },
                ),
              );
            });
          }),
        )
        .subscribe();

      // A teammate or skill was shared with (or un-shared from) this user, or
      // the socket (re)connected after possibly missing such an event. Refresh
      // BOTH stores: the teammate grid is a client-side join of the teammates
      // list and the assistants store (teammates.reducer
      // `selectAllWithAssistants`), so reloading only one leaves the other stale
      // — e.g. a freshly-granted teammate stays hidden until its skills load.
      // Debounced to coalesce bursts (bulk membership edits, org-wide
      // broadcasts, the initial connect) into a single pair of refetches.
      const reloadAccessibleResources = () =>
        this.ngZone.run(() => {
          this.store.dispatch(
            TeammatesActions.loadTeammates({
              locale: this.translateService.getCurrentLang() as 'en' | 'fr',
            }),
          );
          this.store.dispatch(
            fromAssistants.AssistantsActions.loadAssistants(),
          );
        });

      merge(
        this.socket.fromEvent<fromShared.TeammatesUpdatedEventPayload>(
          fromShared.ChatsGatewayEventsType.TeammatesUpdated,
        ),
        this.socket.fromEvent<fromShared.SkillsUpdatedEventPayload>(
          fromShared.ChatsGatewayEventsType.SkillsUpdated,
        ),
        // Re-sync on (re)connect — events missed while disconnected (sleep,
        // network blip, deploy, a background tab) are otherwise lost until a
        // manual reload.
        this.socket.fromEvent('connect'),
      )
        .pipe(
          takeUntilDestroyed(),
          debounceTime(ACCESS_RELOAD_DEBOUNCE_MS),
          tap(reloadAccessibleResources),
        )
        .subscribe();

      this.socket
        .fromEvent<ApiError>('error')
        .pipe(
          takeUntilDestroyed(),
          tap((error) => {
            console.error(`${error.name}: ${error.message}`);
            if (
              error.status === 401 &&
              error.message.includes("didn't include jwt tokens in cookie")
            ) {
              console.warn('Refreshing tokens');
              this.ngZone.run(() => {
                this.store.dispatch(fromAuth.AuthActions.refreshTokens());
              });
            }
          }),
        )
        .subscribe();

      if (isDevMode()) {
        // eslint-disable-next-line no-console
        this.socket.onAny((event) => console.info('[ChatsSocket] %s', event));
      }

      this.socket.on('error', console.error);
      this.socket.connect();
    });
  }

  reconnect() {
    this.socket.disconnect();
    this.socket.connect();
  }
}
